import { useState, useMemo, useEffect } from 'react';
import './Dashboard.css';
import jejuRegionData from '../../assets/jeju_region_en.json';
// import RegionSelector from '../MapDashboard/components/RegionSelector';
import Selector from '../../components/Selector/Selector';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import { useDashboardRealtimeDataV2 } from '../../hooks/useDashboardRealtimeDataV2';
import useRealtimeScheduler from '../../hooks/useRealtimeScheduler';
import { useDRResources } from '../../hooks/queries/drResourceQueries';
import GridLayoutGroup from './components/GridLayoutGroup';
import DashboardLoadingSkeleton from './components/DashboardLoadingSkeleton';
import { CARD_DEFAULTS, CARD_GROUPS, GROUP_INFO, GRID_COLUMNS } from './config/dashboardConfig';
import { createDefaultLayout, normalizeLayout } from './utils/dashboardStorage';
import { apiFormatToLayout, layoutToApiFormat } from './utils/dashboardApiSync';
import { useDashboardCardPositions, useSaveDashboardCardPositions } from '../../hooks/queries/dashboardQueries';
import { useAuthContext } from '../../contexts/AuthContext';
import { findValidPosition } from './utils/gridUtils';
import SmsSendModal from './components/SmsSendModal';
import { isSystemAdmin } from '../../utils/permissions';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const Dashboard = () => {
  // 사용자 정보 가져오기
  const { user } = useAuthContext();
  const userSysId = user?.UserSysId;

  // 편집 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);
  // SMS 발송 모달 상태
  const [smsSendModalOn, setSmsSendModalOn] = useState(false);
  // 기본 레이아웃 생성
  const defaultLayout = useMemo(() => {
    return createDefaultLayout(CARD_DEFAULTS, CARD_GROUPS);
  }, []);

  // API에서 카드 위치 정보 조회
  const { data: apiCardPositions, isLoading: isLoadingPositions } = useDashboardCardPositions(userSysId);

  // 레이아웃 상태 초기화
  const [layout, setLayout] = useState(() => {
    // 초기에는 기본 레이아웃 사용 (API 응답이 오면 useEffect에서 업데이트됨)
    const initialLayout = normalizeLayout(createDefaultLayout(CARD_DEFAULTS, CARD_GROUPS), CARD_GROUPS);
    
    // 모든 그룹에서 비활성화된 카드를 원래 그룹에 맞는 비활성화 그룹으로 이동
    Object.keys(initialLayout).forEach((group) => {
      if (group === CARD_GROUPS.DISABLED_COMMON || group === CARD_GROUPS.DISABLED_DR_RELATED) return;
      const disabledCards = initialLayout[group].filter((card) => !card.enabled);
      if (disabledCards.length > 0) {
        initialLayout[group] = initialLayout[group].filter((card) => card.enabled);
        disabledCards.forEach((card) => {
          // 원래 그룹에 따라 적절한 비활성화 그룹으로 이동
          const originalGroup = card.group || CARD_DEFAULTS[card.type]?.group || CARD_GROUPS.COMMON;
          const targetDisabledGroup = originalGroup === CARD_GROUPS.COMMON 
            ? CARD_GROUPS.DISABLED_COMMON 
            : CARD_GROUPS.DISABLED_DR_RELATED;
          card.group = targetDisabledGroup;
          delete card.order;
          if (!initialLayout[targetDisabledGroup]) {
            initialLayout[targetDisabledGroup] = [];
          }
          initialLayout[targetDisabledGroup].push(card);
        });
      }
      initialLayout[group].forEach((card) => {
        delete card.order;
      });
    });
    
    return initialLayout;
  });

  // API 응답이 변경되면 레이아웃 업데이트
  useEffect(() => {
    if (isLoadingPositions) return; // 로딩 중이면 스킵

    if (apiCardPositions && (apiCardPositions.Total > 0 || apiCardPositions.Items?.length > 0)) {
      // API 응답이 있으면 변환하여 레이아웃 설정
      const apiLayout = apiFormatToLayout(apiCardPositions, defaultLayout);
      setLayout(normalizeLayout(apiLayout, CARD_GROUPS));
    } else {
      // API 응답이 비어있거나 없으면 기본 레이아웃 사용
      setLayout(normalizeLayout(defaultLayout, CARD_GROUPS));
    }
  }, [apiCardPositions, isLoadingPositions, defaultLayout]);

  // 카드 위치 저장 mutation
  const saveCardPositionsMutation = useSaveDashboardCardPositions();

  // 레이아웃 변경 핸들러 (react-grid-layout에서 호출)
  const handleLayoutChange = (groupId, updatedCards) => {
    setLayout((prevLayout) => {
      const newLayout = normalizeLayout({ ...prevLayout }, CARD_GROUPS);
      newLayout[groupId] = updatedCards;
      return newLayout;
    });
  };

  // 편집 완료 버튼 클릭 핸들러
  const handleEditModeToggle = () => {
    if (isEditMode) {
      // 편집 모드 종료 시 API에 저장
      if (userSysId) {
        const apiPayload = layoutToApiFormat(layout, userSysId);
        
        saveCardPositionsMutation.mutate(apiPayload, {
          onSuccess: () => {
          },
          onError: (error) => {
          },
        });
      }
    }
    
    setIsEditMode(!isEditMode);
  };

  // DR 자원 선택 상태
  const [selectedDRResourceId, setSelectedDRResourceId] = useState(null);
  const [selectedDRResource, setSelectedDRResource] = useState(null);

  // DR 자원 목록 조회 (모든 DR 자원 가져오기 - 첫 페이지만)
  const { data: drResourcesData, isLoading: drResourcesLoading } = useDRResources({
    Page: 1,
    PageSize: 1000 // 충분히 큰 값으로 모든 DR 자원 가져오기 시도
  });

  // DR 자원 목록을 Selector 옵션으로 변환
  const drResourceOptions = useMemo(() => {
    if (!drResourcesData?.Items) return [];
    return drResourcesData.Items.map(resource => ({
      id: resource.DrResourceSysId,
      label: resource.ResourceName || resource.DrResourceSysId,
      data: resource
    }));
  }, [drResourcesData]);

  // DR 자원 선택 핸들러
  const handleDRResourceChange = (item) => {
    if (item) {
      setSelectedDRResourceId(item.id);
      setSelectedDRResource(item.data);
    } else {
      setSelectedDRResourceId(null);
      setSelectedDRResource(null);
    }
  };

  // DR 자원 목록이 로드되면 첫 번째 항목 자동 선택
  useEffect(() => {
    if (drResourceOptions.length > 0 && !selectedDRResourceId && !drResourcesLoading) {
      const firstOption = drResourceOptions[0];
      setSelectedDRResourceId(firstOption.id);
      setSelectedDRResource(firstOption.data);
    }
  }, [drResourceOptions, selectedDRResourceId, drResourcesLoading]);

  // localStorage에서 저장된 지역 선택 상태 복원 (주석 처리)
  // const [selectedRegion, setSelectedRegion] = useState(() => {
  //   try {
  //     const savedRegion = localStorage.getItem(STORAGE_KEYS.DASHBOARD_SELECTED_REGION);
  //     if (savedRegion) {
  //       const parsed = JSON.parse(savedRegion);
  //       return {
  //         sido: parsed.sido || '',
  //         sigungu: parsed.sigungu || '',
  //         eupmyeondong: parsed.eupmyeondong || ''
  //       };
  //     }
  //   } catch (error) {
  //     console.warn('Failed to parse saved region from localStorage:', error);
  //   }
  //   return {
  //     sido: '',
  //     sigungu: '',
  //     eupmyeondong: ''
  //   };
  // });
  
  // 지역 선택 상태가 변경될 때마다 localStorage에 저장 (주석 처리)
  // useEffect(() => {
  //   try {
  //     localStorage.setItem(STORAGE_KEYS.DASHBOARD_SELECTED_REGION, JSON.stringify(selectedRegion));
  //   } catch (error) {
  //     console.warn('Failed to save region to localStorage:', error);
  //   }
  // }, [selectedRegion]);
  
  // 선택된 지역의 regionCode 계산 (주석 처리)
  // const regionCode = useMemo(() => {
  //   if (!selectedRegion.eupmyeondong) return null;
  //   
  //   const regionData = jejuRegionData.find(data => 
  //     data.RegionSigungu === selectedRegion.sigungu && 
  //     data.RegionEupMyuonDong === selectedRegion.eupmyeondong
  //   );
  //   
  //   return regionData?.RegionCode ? regionData.RegionCode.toString().slice(0, -2) : null;
  // }, [selectedRegion]);
  
  // 통합 데이터 훅 (V2 - DR 자원 ID 기반)
  const dashboardData = useDashboardRealtimeDataV2(selectedDRResourceId);
  
  // 15분 단위로 실시간 데이터 새로 불러오기
  const {
    nextUpdateTime,
    lastUpdateTime,
    isUpdating,
    manualUpdate
  } = useRealtimeScheduler(
    async (updateTime) => {
      if (!isEditMode && dashboardData.refetchAll) {
        await dashboardData.refetchAll();
      }
    },
    {
      enabled: !isEditMode,
      immediate: true
    }
  );
  
  // const sigunguList = useMemo(() => {
  //   return [...new Set(jejuRegionData.map(item => item.RegionSigungu))];
  // }, []);
  // const eupmyeondongList = useMemo(() => {
  //   return [...new Set(jejuRegionData.map(item => item.RegionEupMyuonDong))];
  // }, []);

  const combinedDashboardData = {
    ...dashboardData,
    nextUpdateTime,
    lastUpdateTime,
    isUpdating: isEditMode ? false : isUpdating,
    manualUpdate: isEditMode ? () => {} : manualUpdate,
    selectedDRResourceId,
    selectedDRResource,
    // selectedRegion (주석 처리)
  };


  // 카드 활성화/비활성화 토글
  const handleCardToggle = (cardId) => {

    setLayout((prevLayout) => {
      // 모든 그룹이 존재하도록 정규화
      const newLayout = normalizeLayout({ ...prevLayout }, CARD_GROUPS);
      let foundCard = null;
      let sourceGroup = null;
      let shouldRefetchData = false; // 활성화 시 데이터 재로드 플래그

      // 모든 그룹에서 카드 찾기 (활성 그룹 먼저 검색)
      Object.keys(newLayout).forEach((group) => {
        if (group === CARD_GROUPS.DISABLED_COMMON || group === CARD_GROUPS.DISABLED_DR_RELATED) return;
        const index = newLayout[group].findIndex((card) => card.id === cardId);
        if (index !== -1) {
          foundCard = { ...newLayout[group][index] };
          sourceGroup = group;
        }
      });

      // 비활성화 그룹에서도 검색
      if (!foundCard) {
        const disabledCommonIndex = newLayout[CARD_GROUPS.DISABLED_COMMON]?.findIndex((card) => card.id === cardId);
        if (disabledCommonIndex !== -1) {
          foundCard = { ...newLayout[CARD_GROUPS.DISABLED_COMMON][disabledCommonIndex] };
          sourceGroup = CARD_GROUPS.DISABLED_COMMON;
        } else {
          const disabledDrIndex = newLayout[CARD_GROUPS.DISABLED_DR_RELATED]?.findIndex((card) => card.id === cardId);
          if (disabledDrIndex !== -1) {
            foundCard = { ...newLayout[CARD_GROUPS.DISABLED_DR_RELATED][disabledDrIndex] };
            sourceGroup = CARD_GROUPS.DISABLED_DR_RELATED;
          }
        }
      }

      if (!foundCard) {
        console.warn('handleCardToggle: Card not found!', { cardId, newLayout });
        return newLayout;
      }

      const newEnabledState = !foundCard.enabled;

      if (newEnabledState) {
        // 활성화 시
        if (sourceGroup === CARD_GROUPS.DISABLED_COMMON || sourceGroup === CARD_GROUPS.DISABLED_DR_RELATED) {
          const originalGroup = CARD_DEFAULTS[foundCard.type]?.group || CARD_GROUPS.COMMON;
          if (!newLayout[originalGroup]) {
            newLayout[originalGroup] = [];
          }
          // 원래 그룹에 따라 적절한 비활성화 그룹에서 제거
          if (sourceGroup === CARD_GROUPS.DISABLED_COMMON) {
            newLayout[CARD_GROUPS.DISABLED_COMMON] = newLayout[CARD_GROUPS.DISABLED_COMMON].filter(
              (card) => card.id !== cardId
            );
          } else {
            newLayout[CARD_GROUPS.DISABLED_DR_RELATED] = newLayout[CARD_GROUPS.DISABLED_DR_RELATED].filter(
              (card) => card.id !== cardId
            );
          }
          
          // 활성화된 카드만 필터링 (현재 활성화하려는 카드 제외)
          const existingCards = newLayout[originalGroup].filter((c) => c.enabled && c.id !== cardId);
          
          // 가장 마지막 행 찾기 (모든 활성화된 카드 중 가장 아래 행)
          let targetX = 1;
          let targetY = 1;
          
          if (existingCards.length > 0) {
            let maxY = 0; // 카드가 차지하는 가장 마지막 행 (0부터 시작)
            existingCards.forEach((card) => {
              if (card.y !== undefined && card.sizeH !== undefined) {
                // 카드가 차지하는 마지막 행 계산
                // 예: y=1, sizeH=3이면 1, 2, 3행을 차지하므로 마지막 행은 3 (y + sizeH - 1)
                const cardBottomRow = (card.y || 1) + (card.sizeH || 1) - 1;
                maxY = Math.max(maxY, cardBottomRow);
              }
            });
            // 마지막 행 + 1 위치에 배치 (다음 빈 행)
            targetY = maxY + 1;
          } else {
            targetY = 1; // 활성화된 카드가 없으면 첫 번째 행에 배치
          }
          
          // 유효한 위치 찾기 (충돌 방지, 필요시 조정)
          const validPosition = findValidPosition(
            targetX,
            targetY,
            foundCard.sizeW || 1,
            foundCard.sizeH || 1,
            existingCards,
            foundCard.id
          );
          
          
          // 카드 활성화 및 원래 그룹에 추가
          const activatedCard = {
            ...foundCard,
            enabled: true,
            group: originalGroup,
            x: validPosition.x,
            y: validPosition.y,
          };
          
          newLayout[originalGroup].push(activatedCard);
          shouldRefetchData = true; // 활성화 시 데이터 재로드 필요
          
        }
      } else {
        // 비활성화 시
        if (sourceGroup !== CARD_GROUPS.DISABLED_COMMON && sourceGroup !== CARD_GROUPS.DISABLED_DR_RELATED) {
          newLayout[sourceGroup] = newLayout[sourceGroup].filter((card) => card.id !== cardId);
          
          // 원래 그룹에 따라 적절한 비활성화 그룹 선택
          const originalGroup = foundCard.group || CARD_DEFAULTS[foundCard.type]?.group || CARD_GROUPS.COMMON;
          const targetDisabledGroup = originalGroup === CARD_GROUPS.COMMON 
            ? CARD_GROUPS.DISABLED_COMMON 
            : CARD_GROUPS.DISABLED_DR_RELATED;
          
          if (!newLayout[targetDisabledGroup]) {
            newLayout[targetDisabledGroup] = [];
          }
          
          // 비활성화 그룹에 추가할 때 순차적 위치 계산 (4열 기준)
          // 기존 비활성화된 카드들의 위치를 고려하여 겹치지 않도록 배치
          const disabledCardsInGroup = newLayout[targetDisabledGroup];
          let targetCol = 1;
          let targetRow = 1;
          let positionFound = false;
          
          // 기존 비활성화된 카드들의 위치 확인
          const occupiedPositions = new Set();
          disabledCardsInGroup.forEach(card => {
            if (card.x && card.y) {
              const posKey = `${card.x},${card.y}`;
              occupiedPositions.add(posKey);
              // 카드의 크기도 고려하여 모든 셀을 occupied로 표시
              for (let dx = 0; dx < (card.sizeW || 1); dx++) {
                for (let dy = 0; dy < (card.sizeH || 1); dy++) {
                  occupiedPositions.add(`${card.x + dx},${card.y + dy}`);
                }
              }
            }
          });
          
          // 빈 위치 찾기 (순차적으로 탐색)
          const cardSizeW = foundCard.sizeW || 1;
          const cardSizeH = foundCard.sizeH || 1;
          
          for (let row = 1; row <= 100; row++) {
            for (let col = 1; col <= GRID_COLUMNS; col++) {
              // 카드 크기만큼 공간이 있는지 확인
              let canPlace = true;
              for (let dx = 0; dx < cardSizeW; dx++) {
                for (let dy = 0; dy < cardSizeH; dy++) {
                  const checkCol = col + dx;
                  const checkRow = row + dy;
                  if (checkCol > GRID_COLUMNS || occupiedPositions.has(`${checkCol},${checkRow}`)) {
                    canPlace = false;
                    break;
                  }
                }
                if (!canPlace) break;
              }
              
              if (canPlace) {
                targetCol = col;
                targetRow = row;
                positionFound = true;
                break;
              }
            }
            if (positionFound) break;
          }
          
          const disabledCard = {
            ...foundCard,
            enabled: false,
            group: targetDisabledGroup,
            x: targetCol,
            y: targetRow,
          };
          
          newLayout[targetDisabledGroup].push(disabledCard);

        }
      }

      // React가 상태 변경을 감지하도록 새로운 객체 참조 생성
      // 각 그룹의 배열도 새로운 참조로 생성하여 React가 변경을 감지하도록 함
      const finalLayout = {};
      Object.keys(newLayout).forEach((group) => {
        finalLayout[group] = [...newLayout[group]]; // 새 배열 참조 생성
      });

      // 활성화 시 데이터 재로드 (편집 모드가 아닐 때만)
      if (shouldRefetchData && !isEditMode && dashboardData.refetchAll) {
        // 비동기로 처리하여 레이아웃 업데이트를 먼저 완료
        setTimeout(() => {
          dashboardData.refetchAll().catch(err => {
            console.error('Failed to refetch data after card activation:', err);
          });
        }, 100);
      }


      return finalLayout;
    });
  };

  // API 로딩 중일 때 스켈레톤 표시
  if (isLoadingPositions) {
    return <DashboardLoadingSkeleton />;
  }

  return (
    <div className="dashboard-page-container page-container">
      {/* 편집 모드 토글 버튼 */}
      <div className="dashboard-header">
        <div className="flex justify-end align-center mb-20 gap-10">
          {isSystemAdmin() && (
            <button className="btn small-btn btn-danger flex align-center" onClick={() => setSmsSendModalOn(true)}>
              SMS 발송
            </button>
          )}
          <button
            onClick={handleEditModeToggle}
            className={`edit-mode-toggle btn small-btn ${isEditMode ? 'active' : ''}`}
            disabled={!userSysId || isLoadingPositions}
          >
            {isEditMode ? '편집 완료' : '편집 모드'}
          </button>
        </div>
        
      </div>

      {/* 공통 그룹 */}
      {(() => {
        const commonCards = layout[CARD_GROUPS.COMMON] || [];

        return (
          <GridLayoutGroup
            groupId={CARD_GROUPS.COMMON}
            groupLabel={GROUP_INFO[CARD_GROUPS.COMMON].label}
            cards={commonCards}
            dashboardData={combinedDashboardData}
            isEditMode={isEditMode}
            onCardToggle={handleCardToggle}
            onLayoutChange={handleLayoutChange}
          />
        );
      })()}
      <div className="flex  align-center mb-20">
        <h1 className="text-h1 text-gray-800">DR 자원 현황</h1>
      </div>
      <div className="flex my-10 gap-10">
        {/* 지역 선택 컴포넌트 (주석 처리) */}
        {/* <div className="rounded">
          <RegionSelector
            selectedRegion={selectedRegion}
            onRegionSelect={setSelectedRegion}
            jejuRegionData={jejuRegionData}
          sigunguList={sigunguList}
          eupmyeondongList={eupmyeondongList}
          isAbsolute={true}
          isRounded={true}
          compact={true}
          />
        </div> */}
        {/* DR 자원 선택 컴포넌트 */}
        <div className="rounded" style={{ minWidth: '300px' }}>
          <Selector
            mode="local"
            size="lg"
            options={drResourceOptions}
            value={selectedDRResource ? {
              id: selectedDRResource.DrResourceSysId,
              label: selectedDRResource.ResourceName || selectedDRResource.DrResourceSysId,
              data: selectedDRResource
            } : null}
            onChange={handleDRResourceChange}
            placeholder="DR 자원을 선택하세요"
            className="w-full"
            enableSearch={true}
            emptyMessage="DR 자원이 없습니다"
            disabled={drResourcesLoading}
          />
        </div>
        <div className="flex align-center bg-primary rounded text-b2-700 text-gray-600 gap-10 px-10 py-5 shadow-dashboard">
          <div>집계기준</div>
          <div className="flex-1">
            {lastUpdateTime ? lastUpdateTime.format('YYYY-MM-DD HH:mm') : '데이터 로딩 중...'}
          </div>
          <button
            onClick={() => {
              if (!isUpdating && !isEditMode) manualUpdate();
            }}
            className="flex align-center p-4 justify-center bg-secondary rounded text-b2-700 text-gray-600 border"
            disabled={isUpdating || isEditMode}
          >
            {isUpdating ? '업데이트 중...' : <RefreshIcon />}
          </button>
        </div>
      </div>
      {/* DR 관련 그룹 */}
      {(() => {
        const drRelatedCards = layout[CARD_GROUPS.DR_RELATED] || [];
        return (
          <GridLayoutGroup
            groupId={CARD_GROUPS.DR_RELATED}
            groupLabel={GROUP_INFO[CARD_GROUPS.DR_RELATED].label}
            cards={drRelatedCards}
            dashboardData={combinedDashboardData}
            isEditMode={isEditMode}
            onCardToggle={handleCardToggle}
            onLayoutChange={handleLayoutChange}
          />
        );
      })()}

      {/* 비활성화된 공통 그룹 (편집 모드에서만 표시) */}
      {(() => {
        const disabledCommonCards = layout[CARD_GROUPS.DISABLED_COMMON] || [];
        
        if (isEditMode && disabledCommonCards.length > 0) {
          return (
            <GridLayoutGroup
              groupId={CARD_GROUPS.DISABLED_COMMON}
              groupLabel={GROUP_INFO[CARD_GROUPS.DISABLED_COMMON].label}
              cards={disabledCommonCards}
              dashboardData={combinedDashboardData}
              isEditMode={isEditMode}
              onCardToggle={handleCardToggle}
              onLayoutChange={handleLayoutChange}
            />
          );
        }
        return null;
      })()}

      {/* 비활성화된 DR 그룹 (편집 모드에서만 표시) */}
      {(() => {
        const disabledDrCards = layout[CARD_GROUPS.DISABLED_DR_RELATED] || [];
        
        if (isEditMode && disabledDrCards.length > 0) {
          return (
            <GridLayoutGroup
              groupId={CARD_GROUPS.DISABLED_DR_RELATED}
              groupLabel={GROUP_INFO[CARD_GROUPS.DISABLED_DR_RELATED].label}
              cards={disabledDrCards}
              dashboardData={combinedDashboardData}
              isEditMode={isEditMode}
              onCardToggle={handleCardToggle}
              onLayoutChange={handleLayoutChange}
            />
          );
        }
        return null;
      })()}
      {smsSendModalOn && <SmsSendModal onClose={() => setSmsSendModalOn(false)} />}
    </div>
  );
};

export default Dashboard;
